import mapboxgl from 'mapbox-gl';
import type { FeatureCollection, Geometry, Position } from 'geojson';

/**
 * Drawn geometry on a Mapbox map — the one implementation shared by the
 * location picker (the shape being drawn) and the entity map (saved shapes).
 * Geometry is plain GeoJSON, the same object `gis_locations.geom` reads and
 * writes through PostgREST (docs/LOCATIONS.md "Drawn geometry").
 */

/** A shape with an optional label (shown on hover with `hoverLabels`). */
export interface LabelledShape {
  geometry: Geometry;
  label?: string;
}

const asShape = (shape: Geometry | LabelledShape): LabelledShape =>
  'geometry' in shape ? shape : { geometry: shape };

/**
 * Create or update a GeoJSON source plus its fill, outline and vertex layers.
 * Idempotent: call it on every change, and again after `setStyle` (a style
 * swap drops every custom source and layer).
 *
 * `hoverLabels`: hovering a shape shows its `label` in a small popup (set as
 * text, never HTML — labels are user-entered names). Bound once, when the
 * layers are created.
 */
export function setShapeLayers(
  map: mapboxgl.Map,
  id: string,
  shapes: Array<Geometry | LabelledShape>,
  color: string,
  options: { hoverLabels?: boolean } = {},
): void {
  const data: FeatureCollection = {
    type: 'FeatureCollection',
    features: shapes
      .map(asShape)
      .filter((shape) => isDrawable(shape.geometry))
      .map(({ geometry, label }) => ({
        type: 'Feature',
        properties: label ? { label } : {},
        geometry,
      })),
  };
  const source = map.getSource(id) as mapboxgl.GeoJSONSource | undefined;
  if (source) {
    source.setData(data);
    return;
  }
  map.addSource(id, { type: 'geojson', data });
  if (options.hoverLabels) bindHoverLabels(map, `${id}-fill`);
  map.addLayer({
    id: `${id}-fill`,
    type: 'fill',
    source: id,
    filter: ['==', ['geometry-type'], 'Polygon'],
    paint: { 'fill-color': color, 'fill-opacity': 0.25 },
  });
  map.addLayer({
    id: `${id}-line`,
    type: 'line',
    source: id,
    filter: ['!=', ['geometry-type'], 'Point'],
    paint: { 'line-color': color, 'line-width': 2 },
  });
  map.addLayer({
    id: `${id}-point`,
    type: 'circle',
    source: id,
    filter: ['==', ['geometry-type'], 'Point'],
    paint: {
      'circle-color': color,
      'circle-radius': 4,
      'circle-stroke-color': '#ffffff',
      'circle-stroke-width': 1,
    },
  });
}

/** Every [lng, lat] position in a geometry, any type. */
function positionsOf(geometry: Geometry): Position[] {
  switch (geometry.type) {
    case 'Point':
      return [geometry.coordinates];
    case 'MultiPoint':
    case 'LineString':
      return geometry.coordinates;
    case 'MultiLineString':
    case 'Polygon':
      return geometry.coordinates.flat();
    case 'MultiPolygon':
      return geometry.coordinates.flat(2);
    case 'GeometryCollection':
      return geometry.geometries.flatMap(positionsOf);
  }
}

/**
 * Whether every position is a finite, in-range lon/lat. The DB's
 * `gis_locations_geom_sane` CHECK guarantees this for stored rows; the guard
 * keeps one bad value (a legacy row, a malformed payload) from throwing inside
 * mapbox-gl — `LngLat` throws on NaN or |lat| > 90 — and blanking the map.
 */
export function isDrawable(geometry: Geometry | null | undefined): geometry is Geometry {
  if (!geometry) return false;
  try {
    const positions = positionsOf(geometry);
    return (
      positions.length > 0 &&
      positions.every(
        ([lng, lat]) =>
          Number.isFinite(lng) && Number.isFinite(lat) && Math.abs(lng) <= 180 && Math.abs(lat) <= 90,
      )
    );
  } catch {
    return false; // not a geometry we can walk (unknown type, missing coordinates)
  }
}

/**
 * Extend `bounds` to cover the whole geometry; an undrawable one (see
 * `isDrawable`) is skipped. Returns `bounds` for chaining.
 */
export function extendBoundsWithGeometry(
  bounds: mapboxgl.LngLatBounds,
  geometry: Geometry,
): mapboxgl.LngLatBounds {
  if (!isDrawable(geometry)) return bounds;
  for (const [lng, lat] of positionsOf(geometry)) bounds.extend([lng, lat]);
  return bounds;
}

/**
 * A ring's corners without consecutive repeats (incl. a trailing copy of the
 * first corner) — the double-click that ends a drawing can leave one.
 */
export function normalizeCorners(corners: Position[]): Position[] {
  const same = (a: Position, b: Position) => a[0] === b[0] && a[1] === b[1];
  const out = corners.filter((c, i) => i === 0 || !same(c, corners[i - 1]));
  while (out.length > 1 && same(out[0], out[out.length - 1])) out.pop();
  return out;
}

/**
 * Whether a ring (corners only, no closing point) is a simple polygon the DB
 * will accept — the client twin of `ST_IsValid` in `gis_locations_geom_sane`,
 * so the picker can ask the user to fix the outline instead of failing at
 * save. Rejects: fewer than 3 distinct corners, a repeated corner (self-touch,
 * spike), zero area (all corners on a line), and any two non-adjacent edges
 * that cross, touch or overlap. Planar test on lon/lat — fine at the scale of
 * a drawn footprint.
 */
export function ringIsSimple(rawCorners: Position[]): boolean {
  const corners = normalizeCorners(rawCorners);
  const n = corners.length;
  if (n < 3) return false;
  if (new Set(corners.map(([x, y]) => `${x},${y}`)).size !== n) return false;

  let twiceArea = 0;
  for (let i = 0; i < n; i++) {
    const [x1, y1] = corners[i];
    const [x2, y2] = corners[(i + 1) % n];
    twiceArea += x1 * y2 - x2 * y1;
  }
  if (Math.abs(twiceArea) < 1e-14) return false;

  const orient = (o: Position, a: Position, b: Position) =>
    Math.sign((a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]));
  const onSegment = (p: Position, a: Position, b: Position) =>
    Math.min(a[0], b[0]) <= p[0] && p[0] <= Math.max(a[0], b[0]) &&
    Math.min(a[1], b[1]) <= p[1] && p[1] <= Math.max(a[1], b[1]);
  // Crossing, touching, or collinear-overlapping segments.
  const segmentsMeet = (p1: Position, p2: Position, q1: Position, q2: Position) => {
    const d1 = orient(q1, q2, p1);
    const d2 = orient(q1, q2, p2);
    const d3 = orient(p1, p2, q1);
    const d4 = orient(p1, p2, q2);
    if (d1 !== d2 && d3 !== d4 && d1 * d2 <= 0 && d3 * d4 <= 0) {
      if (d1 !== 0 || d2 !== 0) return true;
    }
    return (
      (d1 === 0 && onSegment(p1, q1, q2)) ||
      (d2 === 0 && onSegment(p2, q1, q2)) ||
      (d3 === 0 && onSegment(q1, p1, p2)) ||
      (d4 === 0 && onSegment(q2, p1, p2))
    );
  };
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      // Edges sharing a corner (neighbours, and the closing pair) may touch.
      if (j === i + 1 || (i === 0 && j === n - 1)) continue;
      if (segmentsMeet(corners[i], corners[(i + 1) % n], corners[j], corners[(j + 1) % n])) return false;
    }
  }
  return true;
}

/**
 * Show a feature's `label` property in a popup while the pointer is over it.
 * One layer (the fill): binding the outline too made the label blink as the
 * pointer crossed the 2px edge between the two layers.
 */
function bindHoverLabels(map: mapboxgl.Map, layerId: string): void {
  const popup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 8 });
  const hide = () => {
    map.getCanvas().style.cursor = '';
    popup.remove();
  };
  map.on('mousemove', layerId, (e) => {
    const label = e.features?.[0]?.properties?.label;
    if (typeof label !== 'string' || !label) {
      hide();
      return;
    }
    map.getCanvas().style.cursor = 'pointer';
    popup.setLngLat(e.lngLat).setText(label).addTo(map);
  });
  map.on('mouseleave', layerId, hide);
}

/**
 * Paint for the picker's area editor (@mapbox/mapbox-gl-draw `styles`): our
 * pick colour instead of Draw's blue/orange theme. Draw suffixes the ids with
 * `.cold`/`.hot` and adds the sources. The LineString layer draws the first
 * two corners while a shape is being started.
 */
export function pickerDrawStyles(color: string): object[] {
  const vertex = ['all', ['==', '$type', 'Point'], ['==', 'meta', 'vertex'], ['!=', 'mode', 'simple_select']];
  const active = (on: number, off: number) => ['case', ['==', ['get', 'active'], 'true'], on, off];
  return [
    {
      id: 'gl-draw-polygon-fill',
      type: 'fill',
      filter: ['all', ['==', '$type', 'Polygon']],
      paint: { 'fill-color': color, 'fill-opacity': 0.25 },
    },
    {
      id: 'gl-draw-lines',
      type: 'line',
      filter: ['any', ['==', '$type', 'LineString'], ['==', '$type', 'Polygon']],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': color, 'line-width': 2 },
    },
    {
      id: 'gl-draw-vertex-outer',
      type: 'circle',
      filter: vertex,
      paint: { 'circle-radius': active(8, 6), 'circle-color': '#ffffff' },
    },
    {
      id: 'gl-draw-vertex-inner',
      type: 'circle',
      filter: vertex,
      paint: { 'circle-radius': active(5, 4), 'circle-color': color },
    },
    {
      id: 'gl-draw-midpoint',
      type: 'circle',
      filter: ['all', ['==', 'meta', 'midpoint']],
      paint: { 'circle-radius': 4, 'circle-color': color, 'circle-opacity': 0.6 },
    },
  ];
}
